import { useState, useEffect, useCallback } from "react";
import { ethers } from "ethers";
import { toast } from "react-toastify";
import { Info } from "lucide-react";
import { Tooltip } from "react-tooltip";
import { useNativePrices } from "./nativePriceCache";
import ChainSelector from "./ChainSelector";
import { useDepositQuote } from "./useBridgeQuote";
import { trackBridgeStatus } from "./bridgeStatusTracker";
import {
  BRIDGE_CHAINS,
  NIBIRU_EID,
  NIBIRU_USDC_ADAPTER,
  ERC20_ABI,
  OFT_ABI,
  USDC_DECIMALS,
  buildSendParam,
  execFee,
} from "./bridgeConstants";
import {
  buildDepositOptions,
  quoteMinAmountLD,
  ensureChain,
  waitForChain,
  createBridgeSigner,
  isNetworkError,
} from "./bridgeHelpers";
import { truncateWalletAddress } from "../functions/Functions";
import { PurpleButton } from "../Buttons/PurpleButton";

function smartFormat(val) {
  if (!val || val === 0) return "0.00";
  if (val >= 1) return val.toFixed(2);
  let d = 2;
  while (d < 10 && Number(val.toFixed(d)) === 0) d++;
  return val.toFixed(Math.min(d + 1, 10));
}

const MAX_RETRIES = 2;
const RETRY_DELAY = 3000;

export default function DepositFlow({ walletAddress }) {
  const [selectedChain, setSelectedChain] = useState(BRIDGE_CHAINS[0]);
  const [amount, setAmount] = useState("");
  const [balances, setBalances] = useState({});
  const [loadingBalances, setLoadingBalances] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [executionStep, setExecutionStep] = useState("");

  // On-chain quote
  const { quote, loading: quoteLoading, error: quoteError } = useDepositQuote({
    chain: selectedChain,
    amount,
    walletAddress,
  });

  // Fetch USDC balances on all source chains
  const fetchBalances = useCallback(async () => {
    if (!walletAddress) return;
    setLoadingBalances(true);
    const bals = {};

    const promises = BRIDGE_CHAINS.map(async (chain) => {
      try {
        const provider = new ethers.JsonRpcProvider(chain.rpcUrl, {
          chainId: chain.chainId,
          name: chain.id,
          ensAddress: null,
        });
        const contract = new ethers.Contract(chain.usdcAddress, ERC20_ABI, provider);
        const bal = await contract.balanceOf(walletAddress);
        bals[chain.id] = ethers.formatUnits(bal, chain.usdcDecimals || USDC_DECIMALS);
        console.log(`[Bridge] ${chain.name} USDC balance:`, bals[chain.id]);
      } catch (err) {
        console.error(`[Bridge] ${chain.name} balance fetch failed:`, err.message);
        bals[chain.id] = "0";
      }
    });

    await Promise.all(promises);
    setBalances(bals);
    setLoadingBalances(false);
  }, [walletAddress]);

  useEffect(() => {
    fetchBalances();
  }, [fetchBalances]);

  const currentBalance = parseFloat(balances[selectedChain.id] || "0");
  const inputAmount = parseFloat(amount) || 0;

  // Button state
  const getButtonState = () => {
    if (!amount || inputAmount <= 0)
      return { label: "Enter amount", disabled: true };
    if (inputAmount > currentBalance)
      return { label: "Insufficient balance", disabled: true };
    if (quoteLoading)
      return { label: "Estimating...", disabled: true };
    if (quoteError)
      return { label: "Quote unavailable", disabled: true };
    if (executing)
      return { label: executionStep, disabled: true };
    return { label: "Deposit", disabled: false };
  };

  const { label: btnLabel, disabled: btnDisabled } = getButtonState();

  // Native token USD prices (cached 6h, fetched via backend CoinGecko proxy)
  const nativePrices = useNativePrices();

  // Fee display with USD estimate
  const feeUsd = quote && nativePrices[quote.feeSymbol]
    ? (quote.feeDisplay * nativePrices[quote.feeSymbol]).toFixed(2)
    : null;

  // Execute deposit with retry
  const executeDeposit = async (attempt = 0) => {
    const provider = new ethers.BrowserProvider(window.ethereum);

    // Switch to source chain
    setExecutionStep("Switching network...");
    await ensureChain(provider, selectedChain.chainId);
    await waitForChain(provider, selectedChain.chainId);

    const walletSigner = await provider.getSigner();
    const fromAddress = await walletSigner.getAddress();
    const readProvider = new ethers.JsonRpcProvider(selectedChain.rpcUrl);
    const signer = createBridgeSigner(
      provider,
      readProvider,
      selectedChain.chainId,
      fromAddress
    );

    const localDecimals = selectedChain.usdcDecimals || USDC_DECIMALS;
    const tokensToSend = ethers.parseUnits(amount, localDecimals);

    // Check allowance — approve max once so user never sees approve again
    const erc20Read = new ethers.Contract(selectedChain.usdcAddress, ERC20_ABI, readProvider);
    const allowance = await erc20Read.allowance(fromAddress, selectedChain.oftAdapter);
    if (allowance < tokensToSend) {
      setExecutionStep("One-time approval...");
      const erc20Signer = new ethers.Contract(selectedChain.usdcAddress, ERC20_ABI, signer);
      const maxApproval = ethers.MaxUint256;
      const approveTx = await erc20Signer.approve(selectedChain.oftAdapter, maxApproval, {
        chainId: selectedChain.chainId,
      });
      if (typeof approveTx.hash === "string") {
        await approveTx.wait();
      }
    }

    // Build OFT send params
    setExecutionStep("Bridging...");
    const recipientBytes32 = ethers.zeroPadValue(walletAddress.toLowerCase(), 32);
    const options = buildDepositOptions(recipientBytes32);
    const oftRead = new ethers.Contract(selectedChain.oftAdapter, OFT_ABI, readProvider);

    // Fresh quote right before sending
    const { minAmountLD } = await quoteMinAmountLD(
      oftRead,
      NIBIRU_EID,
      recipientBytes32,
      tokensToSend,
      options
    );
    const sendParam = buildSendParam(NIBIRU_EID, recipientBytes32, tokensToSend, minAmountLD, options);
    const quoteSendResult = await oftRead.quoteSend(sendParam, false);
    const nativeFeeCeil = execFee(ethers.toBigInt(quoteSendResult.nativeFee));

    // Execute OFT send
    const oftSigner = new ethers.Contract(selectedChain.oftAdapter, OFT_ABI, signer);
    const tx = await oftSigner.send(
      sendParam,
      [nativeFeeCeil, ethers.toBigInt(0)],
      walletAddress,
      {
        value: nativeFeeCeil,
        gasLimit: ethers.toBigInt(500000),
        chainId: selectedChain.chainId,
      }
    );

    if (typeof tx.hash === "string") {
      await tx.wait();
      return tx.hash;
    }
    throw new Error("OFT send did not return a tx hash");
  };

  const handleDeposit = async () => {
    if (!quote) return;
    setExecuting(true);

    try {
      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
          const txHash = await executeDeposit(attempt);
          setAmount("");
          fetchBalances();

          // Track via LayerZero Scan — shows persistent toast until delivered
          trackBridgeStatus(selectedChain.eid, txHash, "deposit", () => {
            fetchBalances();
            window.dispatchEvent(new CustomEvent("bridge-balance-update"));
          });
          break;
        } catch (err) {
          if (isNetworkError(err) && attempt < MAX_RETRIES) {
            await new Promise((r) => setTimeout(r, RETRY_DELAY));
            continue;
          }
          throw err;
        }
      }
    } catch (err) {
      console.error("Deposit error:", err);
      if (err.code === 4001 || err.code === "ACTION_REJECTED") {
        toast.warn("Transaction cancelled.");
      } else {
        toast.error(err.shortMessage || err.message || "Deposit failed.");
      }
    } finally {
      setExecuting(false);
      setExecutionStep("");

      // Best-effort switch back to Nibiru
      try {
        const provider = new ethers.BrowserProvider(window.ethereum);
        await ensureChain(provider, 6900);
      } catch {}
    }
  };

  const handleMax = () => {
    if (currentBalance > 0) {
      setAmount(
        parseFloat(balances[selectedChain.id] || "0")
          .toFixed(USDC_DECIMALS)
          .replace(/\.?0+$/, "")
      );
    }
  };

  return (
    <div className="flex flex-col gap-[16px]">
      {/* Inner card */}
      <div className="bg-[#f8f8f8] rounded-[12px] border border-[rgba(0,0,0,0.06)] p-4">
        <div className="text-[12px] text-[#888] mb-3 font-mono">
          {truncateWalletAddress(walletAddress)}
        </div>

        <ChainSelector
          chains={BRIDGE_CHAINS}
          selected={selectedChain}
          onSelect={setSelectedChain}
          balances={balances}
          loadingBalances={loadingBalances}
        />

        {/* Amount input */}
        <div className="mt-4 flex items-end justify-between">
          <div>
            <input
              type="text"
              inputMode="decimal"
              placeholder="0.00"
              value={amount}
              onChange={(e) => {
                const val = e.target.value;
                if (/^\d*\.?\d*$/.test(val)) setAmount(val);
              }}
              className="text-[28px] font-semibold text-[#111] bg-transparent outline-none w-[160px] placeholder-[#ccc]"
            />
            <div className="text-[12px] text-[#888] mt-0.5">
              {inputAmount > 0 ? `$${inputAmount.toFixed(2)}` : "$0.00"}
            </div>
          </div>
          <div className="text-right">
            <button
              onClick={handleMax}
              className="text-[12px] font-semibold text-[#5b1dee] border border-[#5b1dee] rounded-[8px] px-3 py-1.5 hover:bg-[#f8f4fc] transition-colors cursor-pointer"
            >
              Max
            </button>
            <div className="text-[11px] text-[#888] mt-1.5">
              Balance:{" "}
              <span className="font-semibold text-[#111]">
                {loadingBalances
                  ? "..."
                  : `${smartFormat(parseFloat(balances[selectedChain.id] || "0"))} USDC`}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Info section */}
      <div className="bg-[#faf8fc] rounded-[12px] border border-[rgba(91,29,238,0.06)] px-4 py-3 flex flex-col gap-2">
        <div className="flex items-center justify-between text-[13px]">
          <span className="text-[#666]">Estimated Time</span>
          <span className="text-[#111] font-medium">~2 min</span>
        </div>
        <div className="flex items-center justify-between text-[13px]">
          <span className="text-[#666] flex items-center gap-1">
            Bridge Fee{" "}
            <Info
              size={12}
              className="text-[#aaa] cursor-help"
              data-tooltip-id="bridge-fee-tooltip"
            />
            <Tooltip
              id="bridge-fee-tooltip"
              place="top"
              style={{ maxWidth: 240, fontSize: 12, zIndex: 999999, backgroundColor: "rgba(255, 255, 255, 0.92)", color: "#333", borderRadius: 10, padding: "8px 12px", backdropFilter: "blur(8px)", boxShadow: "0 2px 12px rgba(0,0,0,0.1)" }}
              content="A small fee paid in the source chain's native token (e.g. ETH, POL) to LayerZero executors for delivering your tokens to Nibiru."
            />
          </span>
          <span className="text-[#111] font-medium">
            {quoteLoading ? (
              <span className="text-[#bbb]">Estimating...</span>
            ) : quote ? (
              <>
                {quote.feeDisplay.toFixed(8)} {quote.feeSymbol}
                {feeUsd && (
                  <span className="text-[#888] font-normal"> (~${feeUsd})</span>
                )}
              </>
            ) : (
              "—"
            )}
          </span>
        </div>
      </div>

      {/* Error */}
      {quoteError && inputAmount > 0 && inputAmount <= currentBalance && (
        <div className="text-[12px] text-red-500 text-center px-2">
          {quoteError}
        </div>
      )}

      {/* CTA */}
      <PurpleButton
        text={
          <span className="flex w-full items-center justify-center gap-2 text-[14px]">
            {(quoteLoading || executing) && (
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            )}
            {btnLabel}
          </span>
        }
        disabled={btnDisabled}
        onClick={handleDeposit}
        className="w-full h-[46px] !rounded-[12px]"
      />
    </div>
  );
}
